import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { CarTypeMappingService } from '@pages/travel/car-hire/services/car-type-mapping.service';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import {
  ICarHireSubBookingResponse,
  ITravelCarHireSubBooking,
} from '@shared/shared/interfaces/travel-car-hire.interfaces';
import { getCarSubBookingData } from '@shared/store/travel-search/selectors/car-hire-search.selectors';
import * as UrlLib from '@shared/utils/url.lib';
import { TRAVEL_CAR_HIRE_SUB_BOOKING_LOCAL_STORAGE_KEY } from 'app.config';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as travelBookingProgressBarActions from '../actions/travel-booking-progress-bar.actions';
import { TravellerInfoResponse } from '../actions/travel-booking.actions';
import * as fromActions from '../actions/travel-car-hire-sub-booking.actions';
import { TravelBookingState } from '../reducers';
import * as fromReducers from '../reducers/travel-car-hire-sub-booking.reducers';
import { getTravelCarHireSubBookingFeatureState } from '../selectors';

@Injectable()
export class TravelCarHireSubBookingEffects implements OnInitEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private ngrxStore: Store<{ 'travel-booking': TravelBookingState }>,
    private carTypeMappingService: CarTypeMappingService,
    private router: Router
  ) {}

  public ngrxOnInitEffects(): Action {
    return new fromActions.RestoreCarHireSubBooking();
  }

  public restoreCarHireSubBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.RESTORE_CAR_HIRE_SUB_BOOKING),
      map(() => localStorage.getItem(TRAVEL_CAR_HIRE_SUB_BOOKING_LOCAL_STORAGE_KEY)),
      filter((localStorageItem) => !!localStorageItem),
      map((localStorageItem) => new fromActions.RestoreCarHireSubBookingSuccess(JSON.parse(localStorageItem)))
    )
  );

  public clearCarHireSubBooking$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_CAR_SUB_BOOKING),
        tap(() => localStorage.removeItem(TRAVEL_CAR_HIRE_SUB_BOOKING_LOCAL_STORAGE_KEY))
      ),
    { dispatch: false }
  );

  public createCarHireSubBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CREATE_CAR_SUB_BOOKING),
      withLatestFrom(this.ngrxStore.select(getCarSubBookingData)),
      switchMap(([, carHireSearchData]: [fromActions.CreateCarSubBooking, any]) => {
        const payload = this.getCarHireSubBookingPayload(carHireSearchData);
        return this.http.post<ICarHireSubBookingResponse>(ApiEndpoint.TRAVEL_CAR_HIRE_SUB_BOOKING, payload).pipe(
          map((res) => {
            this.createSubBookingSuccess(res);
            this.router.navigate([`/booking/travel/`]);
            return new fromActions.CreateCarSubBookingSuccess(res);
          }),
          catchError((err) => of(new fromActions.CreateCarSubBookingFailure(err)))
        );
      })
    )
  );

  public patchCarSubBookingTravellers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.PATCH_CAR_SUB_BOOKING_TRAVELLERS),
      withLatestFrom(this.ngrxStore.select(getTravelCarHireSubBookingFeatureState)),
      switchMap(([action, subBooking]: [fromActions.PatchCarSubBookingTravellers, any]) => {
        const url = UrlLib.replaceTemplateKeys(ApiEndpoint.TRAVEL_CAR_SUB_BOOKING_PATCH_TRAVELLERS, {
          id: subBooking.id,
        });
        const payload = {
          lead_driver: action.payload.leadDriver,
        };
        return this.http.patch<TravellerInfoResponse>(url, payload).pipe(
          map((result) => {
            return new fromActions.PatchCarSubBookingTravellersSuccess(result);
          }),
          catchError((err) => of(new fromActions.PatchCarSubBookingTravellersFailure(err)))
        );
      })
    )
  );

  private getCarHireSubBookingPayload(carHireSearchData: any) {
    return {
      ...carHireSearchData,
      type: this.carTypeMappingService.mapIdsToTitles(carHireSearchData.type),
    };
  }

  public viewCarHireSubBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.VIEW_CAR_SUB_BOOKING),
      withLatestFrom(this.ngrxStore),
      switchMap(([, state$]) => {
        const state = state$['travel-booking'].travelCarHireSubBooking;
        return this.viewSubBooking(state);
      })
    )
  );

  /**
   * Either gets the sub booking info form the state if it's present or makes an API call to get it
   *
   * @param state
   */
  public viewSubBooking(state: ITravelCarHireSubBooking): Observable<any> {
    if (state && state !== fromReducers.initialState) {
      return of(new fromActions.ViewCarSubBookingSuccess(state));
    } else {
      const json = localStorage.getItem(TRAVEL_CAR_HIRE_SUB_BOOKING_LOCAL_STORAGE_KEY);

      if (json !== null) {
        const carHireSubBooking = JSON.parse(json);
        const _endpoint_template = ApiEndpoint.TRAVEL_CAR_HIRE_SUB_BOOKING;
        const _endpoint = UrlLib.replaceTemplateKeys(_endpoint_template, { id: carHireSubBooking.id });
        return this.http.get(_endpoint).pipe(
          map((res: ICarHireSubBookingResponse) => {
            return new fromActions.ViewCarSubBookingSuccess(res);
          }),
          catchError((err) => {
            return of(new fromActions.ViewCarSubBookingFailure(err));
          })
        );
      } else {
        return of(new fromActions.ViewCarSubBookingSuccess(fromReducers.initialState));
      }
    }
  }

  /**
   * Save the sub booking response to local storage and update the local booking
   * @param data
   */
  public createSubBookingSuccess(data: ICarHireSubBookingResponse) {
    localStorage.setItem(TRAVEL_CAR_HIRE_SUB_BOOKING_LOCAL_STORAGE_KEY, JSON.stringify(data));
    this.ngrxStore.dispatch(
      new travelBookingProgressBarActions.UpdateTravelBookingBar({ is_car_hire_details_complete: true })
    );
  }
}
